import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  Avatar,
  Chip,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Collapse,
  useMediaQuery,
  Fab,
} from '@mui/material';
import {
  Search as SearchIcon,
  CurrencyExchange as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  Store as StoreIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Category as CategoryIcon,
  Memory as LCDIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import AddLookupModal from '../components/AddLookupModal';
import EditLookupModal from '../components/EditLookupModal';
import { lookup } from '../services/api';
import { getBrandColor, getBrandLogo, getBrandTextColor } from '../utils/brandUtils';
import { useAuth } from '../services/auth';

function Lookup() {
  const { canCreate, canEdit, canDelete, isViewer } = useAuth();
  const [showFilters, setShowFilters] = useState(false);
  const [lookupData, setLookupData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    brand: '',
    supplier: '',
    part_category: '',
    lcd_type: ''
  });
  const [brands, setBrands] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [partCategories, setPartCategories] = useState([]);
  const [lcdTypes, setLCDTypes] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });
  const [deleting, setDeleting] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    brands: [],
    suppliers: [],
    part_categories: [],
    LcdTypes: []
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isFirstRender = useRef(true);

  // Initial load - fetch filter options and data once
  useEffect(() => {
    fetchFilterOptions();
    fetchLookupData();
  }, []);

  // Fetch data when filters or search changes
  useEffect(() => {
    // Skip on first render (already fetched above)
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Reset to page 0 and fetch
    setPage(0);
    fetchLookupData();
  }, [searchTerm, filters, rowsPerPage]);

  // Fetch data when page changes (but not on first render or when filters change)
  useEffect(() => {
    // Only fetch if page > 0 (pagination click, not filter reset)
    if (page > 0) {
      fetchLookupData();
    }
  }, [page]);

  const fetchLookupData = async () => {
    try {
      setLoading(true);
      const params = {
        limit: rowsPerPage.toString(),
        offset: (page * rowsPerPage).toString(),
        ...Object.fromEntries(Object.entries(filters).filter(([_, value]) => value !== ''))
      };

      // Add search parameter if searchTerm is not empty
      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      const response = await lookup.getQuaipo(params);
      const data = response.data;
      setLookupData(data.Lookup || []);
      setTotal(data.Total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await lookup.getQuaipoFilters();
      const data = response.data;

      setBrands(data.Brands || []);
      setSuppliers(data.Suppliers || []);
      setPartCategories(data.PartCategories || []);
      setLCDTypes(data.LcdTypes || []);

      // Update filter options for the modal
      setFilterOptions({
        brands: data.Brands || [],
        suppliers: data.Suppliers || [],
        part_categories: data.PartCategories || [],
        LcdTypes: data.LcdTypes || []
      });
    } catch (err) {
      console.warn('Failed to fetch filter options:', err);
    }
  };

  const handleAddSuccess = (newLookup) => {
    // Refresh the data and filter options
    fetchLookupData();
    fetchFilterOptions();
  };

  const handleEditClick = (item) => {
    setEditItem(item);
    setShowEditModal(true);
  };

  const handleEditSuccess = (updatedLookup) => {
    // Refresh the data and filter options
    fetchLookupData();
    fetchFilterOptions();
  };

  const handleDeleteClick = (item) => {
    setDeleteDialog({ open: true, item });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.item) return;

    setDeleting(true);
    try {
      await lookup.deleteQuaipo(deleteDialog.item.Id);

      // Refresh the data
      fetchLookupData();
      setDeleteDialog({ open: false, item: null });
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to delete lookup entry');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, item: null });
  };

  const handleFilterChange = (filterKey, value) => {
    setFilters(prev => ({ ...prev, [filterKey]: value }));
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setFilters({ brand: '', supplier: '', part_category: '', lcd_type: '' });
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatPrice = (price) => {
    if (!price) return '-';
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
    }).format(price);
  };

  // Remove local functions since we're now using shared utilities

  const getPriceComparison = (wholesale, retail) => {
    if (!wholesale || !retail) return null;
    const margin = retail - wholesale;
    const marginPercent = (margin / wholesale) * 100;
    return {
      margin,
      marginPercent,
      isHealthy: marginPercent >= 20, // 20% margin considered healthy
    };
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(45deg, #00BFFF 30%, #0080FF 90%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1,
            }}
          >
            Supplier Parts Lookup
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            Quiapo supplier pricing reference and market comparison
          </Typography>
        </Box>
        {canCreate() && !isMobile && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowAddModal(true)}
            sx={{
              background: 'linear-gradient(45deg, #00BFFF 30%, #0080FF 90%)',
              color: '#1a237e',
              px: 3,
              py: 1.5,
            }}
          >
            Add New Lookup
          </Button>
        )}
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar
                  sx={{
                    backgroundColor: `${theme.palette.primary.main}20`,
                    color: theme.palette.primary.main,
                    mr: 2,
                  }}
                >
                  <StoreIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="primary">
                    {total.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Total Items
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar
                  sx={{
                    backgroundColor: `${theme.palette.success.main}20`,
                    color: theme.palette.success.main,
                    mr: 2,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>₱</Typography>
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="success.main">
                    {lookupData.filter(item => item.WholesalePrice && item.RetailPrice).length}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    With Pricing
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar
                  sx={{
                    backgroundColor: `${theme.palette.warning.main}20`,
                    color: theme.palette.warning.main,
                    mr: 2,
                  }}
                >
                  <SearchIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="warning.main">
                    {lookupData.length}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Current Page
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={isMobile ? 10 : 3}>
              <TextField
                fullWidth
                placeholder="Search: iPhone 13 Pro Max, Samsung LCD, battery..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  endAdornment: isMobile && (
                    <IconButton
                      onClick={() => setShowFilters(!showFilters)}
                      size="small"
                      sx={{ color: showFilters ? 'primary.main' : 'text.secondary' }}
                    >
                      <FilterIcon />
                    </IconButton>
                  ),
                }}
              />
            </Grid>
            {isMobile && (
              <Grid item xs={2}>
                <Tooltip title="Clear all filters">
                  <IconButton onClick={clearAllFilters} size="large">
                    <ClearIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
            )}
            {!isMobile && (
              <>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Brand</InputLabel>
                    <Select
                      value={filters.brand}
                      onChange={(e) => handleFilterChange('brand', e.target.value)}
                      label="Brand"
                    >
                      <MenuItem value="">
                        <em>All Brands</em>
                      </MenuItem>
                      {brands.map((brand) => (
                        <MenuItem key={brand} value={brand}>{brand}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Supplier</InputLabel>
                    <Select
                      value={filters.supplier}
                      onChange={(e) => handleFilterChange('supplier', e.target.value)}
                      label="Supplier"
                    >
                      <MenuItem value="">
                        <em>All Suppliers</em>
                      </MenuItem>
                      {suppliers.map((supplier) => (
                        <MenuItem key={supplier} value={supplier}>{supplier}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Part Type</InputLabel>
                    <Select
                      value={filters.part_category}
                      onChange={(e) => handleFilterChange('part_category', e.target.value)}
                      label="Part Type"
                    >
                      <MenuItem value="">
                        <em>All Parts</em>
                      </MenuItem>
                      {partCategories.map((category) => (
                        <MenuItem key={category} value={category}>{category}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <FormControl fullWidth>
                      <InputLabel>LCD Type</InputLabel>
                      <Select
                        value={filters.lcd_type}
                        onChange={(e) => handleFilterChange('lcd_type', e.target.value)}
                        label="LCD Type"
                      >
                        <MenuItem value="">
                          <em>All Types</em>
                        </MenuItem>
                        {lcdTypes.map((type) => (
                          <MenuItem key={type} value={type}>{type}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Tooltip title="Clear all filters">
                      <IconButton onClick={clearAllFilters} size="large">
                        <ClearIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Grid>
              </>
            )}
            <Collapse in={isMobile && showFilters} sx={{ width: '100%' }}>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Brand</InputLabel>
                    <Select
                      value={filters.brand}
                      onChange={(e) => handleFilterChange('brand', e.target.value)}
                      label="Brand"
                    >
                      <MenuItem value="">
                        <em>All Brands</em>
                      </MenuItem>
                      {brands.map((brand) => (
                        <MenuItem key={brand} value={brand}>{brand}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Supplier</InputLabel>
                    <Select
                      value={filters.supplier}
                      onChange={(e) => handleFilterChange('supplier', e.target.value)}
                      label="Supplier"
                    >
                      <MenuItem value="">
                        <em>All Suppliers</em>
                      </MenuItem>
                      {suppliers.map((supplier) => (
                        <MenuItem key={supplier} value={supplier}>{supplier}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Part Type</InputLabel>
                    <Select
                      value={filters.part_category}
                      onChange={(e) => handleFilterChange('part_category', e.target.value)}
                      label="Part Type"
                    >
                      <MenuItem value="">
                        <em>All Parts</em>
                      </MenuItem>
                      {partCategories.map((category) => (
                        <MenuItem key={category} value={category}>{category}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>LCD Type</InputLabel>
                    <Select
                      value={filters.lcd_type}
                      onChange={(e) => handleFilterChange('lcd_type', e.target.value)}
                      label="LCD Type"
                    >
                      <MenuItem value="">
                        <em>All Types</em>
                      </MenuItem>
                      {lcdTypes.map((type) => (
                        <MenuItem key={type} value={type}>{type}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Collapse>
          </Grid>
        </CardContent>
      </Card>

      {/* Lookup Table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress />
              <Typography sx={{ mt: 2 }}>Searching supplier database...</Typography>
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ m: 2 }}>
              {error}
            </Alert>
          ) : (
            <>
              {/* Desktop Table View */}
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Product Model</TableCell>
                        <TableCell>Brand</TableCell>
                        <TableCell>Part Type</TableCell>
                        <TableCell>LCD Type</TableCell>
                        <TableCell>Supplier</TableCell>
                        <TableCell align="right">Wholesale Price</TableCell>
                        <TableCell align="right">Retail Price</TableCell>
                        <TableCell align="center">Margin</TableCell>
                        {!isViewer() && <TableCell align="center">Actions</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lookupData.map((item) => {
                          const priceComp = getPriceComparison(item.WholesalePrice, item.RetailPrice);

                          return (
                            <TableRow key={item.Id} hover>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Avatar
                                    sx={{
                                      width: 40,
                                      height: 40,
                                      mr: 2,
                                      backgroundColor: getBrandColor(item.Brand),
                                      color: getBrandTextColor(item.Brand),
                                      fontSize: '0.875rem',
                                      fontWeight: 600,
                                    }}
                                  >
                                    {getBrandLogo(item.Brand)}
                                  </Avatar>
                                  <Typography variant="subtitle2" fontWeight={600}>
                                    {item.Model}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                {item.Brand ? (
                                  <Chip
                                    label={item.Brand}
                                    size="small"
                                    sx={{
                                      backgroundColor: `${getBrandColor(item.Brand)}40`,
                                      color: getBrandTextColor(item.Brand),
                                      fontWeight: 600,
                                      border: `1px solid ${getBrandColor(item.Brand)}60`,
                                    }}
                                  />
                                ) : (
                                  <Typography variant="body2" color="textSecondary">
                                    No Brand
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <CategoryIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} />
                                  <Chip
                                    label={item.PartCategory || 'Other'}
                                    size="small"
                                    color="secondary"
                                    variant="filled"
                                  />
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <LCDIcon sx={{ mr: 1, color: 'info.main', fontSize: 18 }} />
                                  <Chip
                                    label={item.LcdType || 'N/A'}
                                    size="small"
                                    color="info"
                                    variant="outlined"
                                  />
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="textSecondary">
                                  {item.supplier || 'ASHTEL'}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography
                                  variant="body1"
                                  fontWeight={600}
                                  color={item.WholesalePrice ? 'text.primary' : 'text.secondary'}
                                >
                                  {formatPrice(item.WholesalePrice)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography
                                  variant="body1"
                                  fontWeight={600}
                                  color={item.RetailPrice ? 'primary' : 'text.secondary'}
                                >
                                  {formatPrice(item.RetailPrice)}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                {priceComp ? (
                                  <Box>
                                    <Typography
                                      variant="body2"
                                      fontWeight={600}
                                      color={priceComp.isHealthy ? 'success.main' : 'warning.main'}
                                    >
                                      {formatPrice(priceComp.margin)}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color={priceComp.isHealthy ? 'success.main' : 'warning.main'}
                                    >
                                      {priceComp.marginPercent.toFixed(1)}%
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Typography variant="body2" color="textSecondary">
                                    -
                                  </Typography>
                                )}
                              </TableCell>
                              {!isViewer() && (
                                <TableCell align="center">
                                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                    {canEdit() && (
                                      <Tooltip title="Edit entry">
                                        <IconButton
                                          onClick={() => handleEditClick(item)}
                                          size="small"
                                          sx={{
                                            color: 'primary.main',
                                            '&:hover': {
                                              backgroundColor: 'primary.light',
                                              opacity: 0.1,
                                            },
                                          }}
                                        >
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    {canDelete() && (
                                      <Tooltip title="Delete entry">
                                        <IconButton
                                          onClick={() => handleDeleteClick(item)}
                                          size="small"
                                          sx={{
                                            color: 'error.main',
                                            '&:hover': {
                                              backgroundColor: 'error.light',
                                              opacity: 0.1,
                                            },
                                          }}
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </Box>
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* Mobile Card View */}
              <Box sx={{ display: { xs: 'block', md: 'none' }, p: 2 }}>
                <Grid container spacing={2}>
                  {lookupData.map((item) => {
                    const priceComp = getPriceComparison(item.WholesalePrice, item.RetailPrice);
                    return (
                      <Grid item xs={12} key={item.Id}>
                        <Card sx={{
                          background: 'linear-gradient(145deg, #1A1F2E 0%, #2A2F3E 100%)',
                          border: '1px solid rgba(0, 191, 255, 0.2)',
                          '&:hover': {
                            border: '1px solid rgba(0, 191, 255, 0.4)',
                            transform: 'translateY(-2px)',
                          },
                          transition: 'all 0.3s ease-in-out',
                        }}>
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Avatar
                                sx={{
                                  width: 32,
                                  height: 32,
                                  mr: 2,
                                  backgroundColor: getBrandColor(item.Brand),
                                  color: getBrandTextColor(item.Brand),
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                }}
                              >
                                {getBrandLogo(item.Brand)}
                              </Avatar>
                              <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="subtitle1" fontWeight={600} noWrap>
                                  {item.Model}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                  {item.Brand && (
                                    <Chip
                                      label={item.Brand}
                                      size="small"
                                      sx={{
                                        backgroundColor: `${getBrandColor(item.Brand)}40`,
                                        color: getBrandTextColor(item.Brand),
                                        fontWeight: 600,
                                        border: `1px solid ${getBrandColor(item.Brand)}60`,
                                        height: 20,
                                        fontSize: '0.7rem',
                                      }}
                                    />
                                  )}
                                  <Chip
                                    label={item.PartCategory || 'Other'}
                                    size="small"
                                    color="secondary"
                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                  />
                                  {item.LcdType && (
                                    <Chip
                                      label={item.LcdType}
                                      size="small"
                                      color="info"
                                      variant="outlined"
                                      sx={{ height: 20, fontSize: '0.7rem' }}
                                    />
                                  )}
                                </Box>
                              </Box>
                              {!isViewer() && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  {canEdit() && (
                                    <IconButton
                                      onClick={() => handleEditClick(item)}
                                      size="small"
                                      sx={{ color: 'primary.main', p: 0.5 }}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  )}
                                  {canDelete() && (
                                    <IconButton
                                      onClick={() => handleDeleteClick(item)}
                                      size="small"
                                      sx={{ color: 'error.main', p: 0.5 }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  )}
                                </Box>
                              )}
                            </Box>

                            <Grid container spacing={2} sx={{ mb: 1 }}>
                              <Grid item xs={6}>
                                <Typography variant="caption" color="textSecondary">
                                  Wholesale
                                </Typography>
                                <Typography variant="h6" fontWeight={600} color="text.primary">
                                  {formatPrice(item.WholesalePrice)}
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="caption" color="textSecondary">
                                  Retail
                                </Typography>
                                <Typography variant="h6" fontWeight={600} color="primary">
                                  {formatPrice(item.RetailPrice)}
                                </Typography>
                              </Grid>
                            </Grid>

                            {priceComp && (
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" color="textSecondary">
                                  Margin: {formatPrice(priceComp.margin)} ({priceComp.marginPercent.toFixed(1)}%)
                                </Typography>
                                <Chip
                                  label={priceComp.isHealthy ? 'Healthy' : 'Low'}
                                  size="small"
                                  color={priceComp.isHealthy ? 'success' : 'warning'}
                                  sx={{ fontSize: '0.7rem', height: 20 }}
                                />
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>

              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 20, 50, 100]}
                sx={{ borderTop: 1, borderColor: 'divider' }}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Mobile FAB for Add */}
      {canCreate() && isMobile && (
        <Fab
          color="primary"
          onClick={() => setShowAddModal(true)}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            background: 'linear-gradient(45deg, #00BFFF 30%, #0080FF 90%)',
          }}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this lookup entry?
            {deleteDialog.item && (
              <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.800', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ color: 'white' }}>
                  <strong>Model:</strong> {deleteDialog.item.Model}
                </Typography>
                <Typography variant="body2" sx={{ color: 'white' }}>
                  <strong>Brand:</strong> {deleteDialog.item.Brand || 'N/A'}
                </Typography>
                <Typography variant="body2" sx={{ color: 'white' }}>
                  <strong>Supplier:</strong> {deleteDialog.item.supplier}
                </Typography>
              </Box>
            )}
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              This action cannot be undone.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDeleteCancel}
            disabled={deleting}
            variant="outlined"
            sx={{
              borderColor: 'grey.400',
              color: '#000000',
              '&:hover': {
                borderColor: 'grey.600',
                backgroundColor: 'grey.50'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
            sx={{
              color: '#000000'
            }}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Lookup Modal */}
      <AddLookupModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={handleAddSuccess}
        filterOptions={filterOptions}
      />

      {/* Edit Lookup Modal */}
      <EditLookupModal
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSuccess={handleEditSuccess}
        filterOptions={filterOptions}
        item={editItem}
      />
    </Box>
  );
}

export default Lookup;